import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Logo } from '../../components/Logo.jsx';
import { AuthShowcase } from './AuthShowcase.jsx';

const EASE = [0.2, 0.7, 0.2, 1];

/**
 * §13: "Split layout, inline errors, no dead ends."
 *
 * The left half is the form and nothing else — this is the one screen where a
 * person has a single job, and anything competing with the email field is in the
 * way. The right half carries the product, and disappears below 1024px rather
 * than stacking underneath: on a phone, a sign-in form should be the first thing
 * on the page, not the second.
 */
export function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <div className="grid min-h-screen bg-surface lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      <div className="relative flex flex-col overflow-hidden px-5 py-8 sm:px-10 lg:px-12 lg:py-10">
        {/*
          * The name set enormous and clipped by the bottom of the column — the
          * same signature Orbit's sign-in carries, on web and on the phone.
          *
          * It is doing real work: an auth screen is two inputs on an empty page,
          * and without something anchoring it the page reads as unfinished
          * rather than as calm. At 5% ink it is felt more than read; anything
          * darker starts competing with the fields.
          */}
        <svg
          aria-hidden="true"
          viewBox="0 0 1000 258"
          preserveAspectRatio="xMidYMax meet"
          className="pointer-events-none absolute inset-x-0 bottom-0 select-none"
        >
          <text
            x="500"
            y="280"
            textAnchor="middle"
            fontSize="300"
            fontWeight="600"
            letterSpacing="-14"
            fill="rgb(var(--ink))"
            fillOpacity="0.05"
          >
            DistrxPay
          </text>
        </svg>

        <Link to="/login" className="relative inline-flex self-start"><Logo /></Link>

        <motion.div
          className="relative flex flex-1 items-center justify-center py-10"
          // Visible on first paint; only the position moves. A sign-in form that
          // needs an animation frame to exist is a sign-in form people reload.
          initial={{ y: 8 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.34, ease: EASE }}
        >
          <div className="w-full max-w-sm">
            <h1 className="text-[26px] font-semibold tracking-tight text-ink">{title}</h1>
            {subtitle && <p className="mt-1.5 text-sm leading-relaxed text-muted">{subtitle}</p>}

            <div className="mt-7">{children}</div>

            {/* "No dead ends": every auth screen offers the way out of it. */}
            {footer && (
              <div
                className="mt-7 border-t pt-5 text-sm text-muted"
                style={{ borderColor: 'rgb(var(--line-soft))' }}
              >
                {footer}
              </div>
            )}
          </div>
        </motion.div>

        <p className="relative text-center text-[11px] leading-relaxed text-faint lg:text-left">
          Protected by rate limiting and account lockout. We never see a card number.
        </p>
      </div>

      <AuthShowcase />
    </div>
  );
}

export default AuthLayout;
